import {
  completeGatekeeperPrecisions,
  isQuestionUserMessage,
} from "@/lib/qualification-lifecycle/metadata-part";
import { stripDustMentionPrefix } from "@/lib/ui-helper";
import type {
  ConversationQuestion,
  GatekeeperPrecisionPair,
  Message,
} from "@/types";

export type GatekeeperPrecisionDbItem = {
  dustMessageId: string;
  gatekeeperPrecisions: GatekeeperPrecisionPair[];
};

function normalizePrecisionText(text: string): string {
  return stripDustMentionPrefix(text).trim().toLowerCase();
}

function collectPrecisionReplyTexts(
  messages: Message[],
  dbItems: GatekeeperPrecisionDbItem[]
): Set<string> {
  const replies = new Set<string>();
  for (const item of dbItems) {
    for (const pair of completeGatekeeperPrecisions(
      item.gatekeeperPrecisions
    )) {
      replies.add(normalizePrecisionText(pair.userReply));
    }
  }
  for (const msg of messages) {
    for (const pair of completeGatekeeperPrecisions(msg.gatekeeperPrecisions)) {
      replies.add(normalizePrecisionText(pair.userReply));
    }
  }
  return replies;
}

function findFallbackQualifiedQuestionIndex(
  messages: Message[],
  item: GatekeeperPrecisionDbItem
): number {
  const byId = messages.findIndex((m) => String(m.id) === item.dustMessageId);
  if (byId >= 0) return byId;

  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role === "user" && msg.content.includes("[[")) {
      return i;
    }
  }

  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "user") return i;
  }

  return -1;
}

function dedupeRawQuestionBeforeQualified(messages: Message[]): Message[] {
  const dropIds = new Set<string>();

  for (let i = 0; i < messages.length - 1; i++) {
    const current = messages[i];
    const next = messages[i + 1];
    if (current.role !== "user" || next.role !== "user") continue;

    const nextIsQualified =
      next.content.includes("[[") ||
      Boolean(completeGatekeeperPrecisions(next.gatekeeperPrecisions).length);
    const currentIsRaw = !current.content.includes("[[");

    if (nextIsQualified && currentIsRaw) {
      dropIds.add(String(current.id));
    }
  }

  if (dropIds.size === 0) return messages;
  return messages.filter((msg) => !dropIds.has(String(msg.id)));
}

export function normalizeGatekeeperTranscript(
  messages: Message[],
  dbItems: GatekeeperPrecisionDbItem[] = []
): Message[] {
  if (messages.length === 0) return messages;

  const precisionReplies = collectPrecisionReplyTexts(messages, dbItems);
  const byQualifiedId = new Map(
    dbItems.map(
      (item) => [item.dustMessageId, item.gatekeeperPrecisions] as const
    )
  );

  let normalized = messages.filter((msg) => {
    if (msg.role !== "user") return true;
    if (completeGatekeeperPrecisions(msg.gatekeeperPrecisions).length > 0) {
      return true;
    }
    return !precisionReplies.has(normalizePrecisionText(msg.content));
  });

  normalized = normalized.map((msg) => {
    const fromDb = byQualifiedId.get(String(msg.id));
    const precisions = fromDb ?? msg.gatekeeperPrecisions;
    if (!precisions?.length) return msg;
    return {
      ...msg,
      gatekeeperPrecisions: precisions,
      gatekeeperPhase: msg.gatekeeperPhase ?? "qualified",
      userIntent: "question" as const,
    };
  });

  for (const item of dbItems) {
    if (normalized.some((m) => String(m.id) === item.dustMessageId)) continue;
    const targetIndex = findFallbackQualifiedQuestionIndex(normalized, item);
    if (targetIndex < 0) continue;
    normalized[targetIndex] = {
      ...normalized[targetIndex],
      gatekeeperPrecisions: item.gatekeeperPrecisions,
      gatekeeperPhase: "qualified",
      userIntent: "question",
    };
  }

  return dedupeRawQuestionBeforeQualified(normalized);
}

export function deriveConversationQuestionsFromMessages(
  messages: Message[]
): ConversationQuestion[] {
  return messages.filter(isQuestionUserMessage).map((msg) => ({
    id: String(msg.id),
    content: msg.content,
    timestamp: msg.timestamp,
  }));
}
